'use client';
import axios from 'axios';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useEffect, useRef, useState } from 'react';
import SearchIcon from '@repo/ui/src/components/Icons/SearchIcon';
import useUpdateEffect from '@hooks/useUpdateEffect';
import { classNames } from '@repo/ui/src/utils/classNames';

const SearchAutocomplete = ({ placeholder, searchTerm, onSearch, className }) => {
  const [value, setValue] = useState(searchTerm || null);
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const controller = useRef(null);
  const { t } = useTranslation();

  const handleSearchClick = () => {
    setValue(inputValue);
    onSearch(inputValue);
    setOpen(false);
  };

  const getTypeaheadValues = async () => {
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = new AbortController();

    try {
      const results = await axios('/api/search/searchTypeAhead', {
        params: { searchTerm: inputValue },
        signal: controller.current.signal,
      }).catch(() => {
        // catch canceled err
        return null;
      });
      controller.current = null;
      return results;
    } catch (error) {
      console.error(error);
      // Do nothing - UI won't show typeahead results
    }
  };

  const handleInput = (value) => {
    setOpen(true);
    setInputValue(value);
  };

  useUpdateEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    setLoading(true);
    getTypeaheadValues().then((res) => {
      if (active) {
        let newOptions = [];

        if (res?.data) {
          newOptions = [...newOptions, ...(res?.data.map((x) => x.query) ?? [])];
        }

        setOptions(newOptions);
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue]);

  useEffect(() => {
    setValue(searchTerm);
    setInputValue(searchTerm);
  }, [searchTerm]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSearchClick();
      }}
      className={classNames('tw-relative tw-w-full', className)}
      data-testid="searchAutoComplete"
    >
      <label id="search-label" htmlFor="search-input" className="tw-sr-only">
        {placeholder || 'Search'}
      </label>
      <div id="instructions" className="tw-hidden">
        Begin typing to search, use arrow keys to navigate, Enter to select
      </div>
      <div className="tw-relative tw-w-full">
        <input
          id="search-input"
          type="text"
          className="tw-w-full tw-border-2 tw-border-gray-300 tw-py-4 tw-px-4 focus:tw-outline-none focus:tw-outline-2 focus:tw-ring focus:tw-ring-brand-200 tw-placeholder-gray-500"
          placeholder={placeholder || 'Search'}
          value={inputValue}
          onChange={(e) => handleInput(e.target.value)}
          aria-labelledby="search-label"
          aria-haspopup="listbox"
          aria-controls="results"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-describedby="instructions"
          data-testid="searchAutoCompleteInput"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        />
        <button
          onClick={handleSearchClick}
          type="button"
          className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-cursor-pointer"
        >
          <SearchIcon className="tw-w-5 tw-h-5 tw-text-gray-500" />
        </button>
      </div>
      {open && options.length > 0 && (
        <ul
          id="results"
          className="tw-absolute tw-list-none tw-pl-0 tw-py-2 tw-w-full tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-shadow-lg tw-z-10"
        >
          {loading ? (
            <li className="tw-w-full tw-px-3 tw-py-3 tw-text-gray-500">
              {t('genericContent.loadingLabel', undefined, { default: 'Loading...' })}
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={index}
                className="tw-w-full tw-px-3 tw-py-3 tw-cursor-pointer hover:tw-bg-gray-100"
                onMouseDown={() => {
                  setValue(option);
                  setInputValue(option);
                  onSearch(option);
                  setOpen(false);
                }}
              >
                {option}
              </li>
            ))
          )}
        </ul>
      )}
    </form>
  );
};

export default SearchAutocomplete;
